import { useNavigate } from 'react-router-dom';
import { FormFieldValue } from '../forms/FormFieldValue';
import { SubmitEvent, useCallback, useState } from 'react';
import { Box, Button, Grid, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import NumberField from '../NumberField';
import { Batch } from '../../models/Batch';

export interface BatchFormState {
  values: Partial<Omit<Batch, 'id'>>;
  errors: Partial<Record<keyof BatchFormState['values'], string>>;
}

export interface BatchFormProps {
  formState: BatchFormState;
  onFieldChange: (
    name: keyof BatchFormState['values'],
    value: FormFieldValue,
  ) => void;
  onSubmit: (formValues: Partial<BatchFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<BatchFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
}

export default function BatchForm(props: BatchFormProps) {
  const {
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit],
  );

  const handleNumberFieldChange = useCallback(
    (value: number | null, fieldName: string) => {
      onFieldChange(
        fieldName as keyof BatchFormState['values'],
        value || 0,
      );
    },
    [onFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? '/dashboard/items');
  }, [navigate, backButtonPath]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <div>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              value={formValues.amount || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'amount')}
              name="quantity"
              label="Quantity"
              error={!!formErrors.amount}
              helperText={formErrors?.amount ?? ' '}
              weightFieldUnits="lbs"
              fullWidth
            />
          </Grid>
        </Grid>
      </div>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="success"
          startIcon={<SaveIcon />}
          loading={isSubmitting}
        >
          {submitButtonLabel}
        </Button>
      </Stack>
    </Box>
  );
}
